/**
 * @file ContactPage.jsx
 * @description Official contact page for SimpleCode. Features a streamlined contact form 
 *              and community links in a clean, Bento-inspired light theme.
 * @module components/ContactPage
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from './layout/Navbar';
import Footer from './layout/Footer';
import {
    FiMail,
    FiUser,
    FiMessageSquare,
    FiSend,
    FiCheckCircle,
    FiAlertCircle,
    FiInstagram,
    FiYoutube,
    FiChevronRight,
    FiHeadphones,
    FiMessageCircle,
    FiCheck,
    FiX
} from 'react-icons/fi';
import { publicApiPost } from '../utils/apiUtils';

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component ContactPage
 * @description Renders the platform's contact form and direct support information.
 *
 * @param {Object} props
 * @param {Function} props.navigate - Router navigation function.
 * @returns {JSX.Element} The rendered Contact page.
 */
const ContactPage = ({ navigate }) => {
    const { isAuthenticated } = useAuth();
    // ── Component State ──
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [loading, setLoading] = useState(true);

    /* ══════════════════════════════════════════════════════════════
     * DATA & CONFIG
     * ══════════════════════════════════════════════════════════════ */

    // ── Navigation Links (Synchronized across the platform) ──
    const navLinks = [
        { id: "home", label: "Home", action: () => navigate("/") },
        { id: "learning", label: "Learning", action: () => navigate("/learning") },
        { id: "dashboard", label: "Dashboard", action: () => navigate("/dashboard"), show: isAuthenticated },
        { id: "dsa", label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { id: "system-design", label: "System Design", action: () => navigate("/system-design") }
    ].filter(link => link.show !== false);

    const footerLinks = [
        { label: "Home", action: () => navigate("/") },
        { label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { label: "System Design", action: () => navigate("/system-design") }
    ];

    // Emulate a light loading state for smooth entry
    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = "SimpleCode | Get in Touch";
        const timer = setTimeout(() => setLoading(false), 400);
        return () => {
            clearTimeout(timer);
            document.title = "SimpleCode";
        };
    }, []);

    // Auto-dismiss success/error messages after 5 seconds
    useEffect(() => {
        if (submitStatus) {
            const timer = setTimeout(() => setSubmitStatus(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [submitStatus]);

    /* ══════════════════════════════════════════════════════════════
     * EVENT HANDLERS
     * ══════════════════════════════════════════════════════════════ */

    /**
     * Updates form data state dynamically based on input changes.
     * @param {React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>} e
     */
    const handleChange = (e) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    /**
     * Handles the contact form submission by posting to the public contact API.
     * @param {React.FormEvent} e
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setSubmitStatus(null);

        try {
            const response = await publicApiPost('/contact', {
                name: formData.name,
                email: formData.email,
                subject: formData.subject,
                message: formData.message
            });

            const result = await response.json();

            if (result.success) {
                setSubmitStatus('success');
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setFormData({ name: '', email: '', subject: '', message: '' });
            } else {
                throw new Error(result.message || 'Failed to send message');
            }
        } catch (error) {
            console.error('Contact form error:', error);
            setSubmitStatus('error');
            // Error handling UI is handled by submitStatus
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-accent flex flex-col font-sans">
                <Navbar navigate={navigate} navLinks={navLinks} />
                <main className="flex-1 pt-16">
                    <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 animate-pulse mt-8">
                        <div className="h-10 w-64 bg-emerald-100 rounded mb-8" />
                        <div className="bg-white rounded-lg p-8 shadow-sm space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="h-12 bg-gray-50 rounded" />
                                <div className="h-12 bg-gray-50 rounded" />
                            </div>
                            <div className="h-12 bg-gray-50 rounded" />
                            <div className="h-32 bg-gray-50 rounded" />
                            <div className="h-12 w-40 bg-emerald-100 rounded" />
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-accent text-foreground flex flex-col font-sans">
            <Navbar navigate={navigate} navLinks={navLinks} />

            <main className="flex-1 pt-16">

                {/* ── Contact Header ── */}
                <section className="bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 mb-8 mt-4">
                    <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 py-8">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <h1 className="text-left text-3xl font-bold text-foreground underline decoration-dotted decoration-foreground underline-offset-8">
                                Get in <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent underline decoration-dotted decoration-emerald-600 ml-1">Touch</span>
                            </h1>
                            <p className="text-muted-foreground text-sm font-medium text-left">We'd love to hear from you</p>
                        </div>
                    </div>
                </section>

                <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 pb-20">
                        <div className="bg-white border-l-4 border-emerald-600 rounded overflow-hidden relative p-8">

                            {/* Background Watermark */}
                            <div className="hidden md:block absolute right-[-10px] top-[-10px] opacity-[0.1] transform rotate-12 transition-opacity duration-300 pointer-events-none z-20">
                                <FiMessageSquare className="w-48 h-48" />
                            </div>

                            <div className="relative z-10">
                                {/* Introduction */}
                                <div className="mb-10 text-left">
                                    <p className="text-muted-foreground leading-relaxed text-sm lg:text-base">
                                        Have questions, feedback, or suggestions? Fill out the form below and our team will get back to you as soon as possible.<br />
                                        Or email directly us at: <a href="mailto:support@simplecode.co.in" className="text-emerald-600 font-semibold hover:underline transition-all">support@simplecode.co.in</a>
                                    </p>
                                </div>

                                {/* Status Messages */}
                                {submitStatus === 'success' && (
                                    <div className="mb-8 p-4 bg-emerald-600 rounded flex items-center space-x-4 animate-fade-in">
                                        <div>
                                            <h3 className="text-white font-bold text-left flex items-center gap-2">Message Sent Successfully! <FiCheck className="text-xl text-white flex-shrink-0" /></h3>
                                            <p className="text-white text-xs mt-1 text-left">Thank you for reaching out. We'll get back to you soon.</p>
                                        </div>
                                    </div>
                                )}

                                {submitStatus === 'error' && (
                                    <div className="mb-8 p-4 bg-red-600 rounded flex items-center space-x-4 animate-fade-in">
                                        <div>
                                            <h3 className="text-red-800 font-bold text-left flex items-center gap-2">Failed to Send Message <FiX className="text-xl text-white flex-shrink-0" /></h3>
                                            <p className="text-red-600 text-xs mt-1 text-left">Something went wrong. Please try again or email us directly.</p>
                                        </div>
                                    </div>
                                )}

                                {/* Contact Form */}
                                <form onSubmit={handleSubmit} className="space-y-8 text-left max-w-full">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        {/* Name */}
                                        <div className="relative group">
                                            <label htmlFor="name" className="flex items-start gap-2 text-sm font-bold text-emerald-600 uppercase tracking-wider mb-2 transition-colors group-focus-within:text-emerald-700">
                                                {/* <FiUser className='h-4 w-4'/> */}
                                                Name
                                            </label>
                                            <div className="flex items-center border-b-2 border-gray-100 group-focus-within:border-emerald-600 transition-colors bg-accent rounded-t px-2" >
                                                <input
                                                    type="text"
                                                    id="name"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    required
                                                    placeholder="Your full name"
                                                    className="w-full bg-transparent py-3 text-sm text-foreground focus:outline-none placeholder-gray-400"
                                                />
                                            </div>
                                        </div>

                                        {/* Email */}
                                        <div className="relative group">
                                            <label htmlFor="email" className="flex items-start gap-2 text-sm font-bold text-emerald-600 uppercase tracking-wider mb-2 transition-colors group-focus-within:text-emerald-700">
                                                {/* <FiMail className="h-4 w-4"/> */}
                                                Email Address
                                            </label>
                                            <div className="flex items-center border-b-2 border-gray-100 group-focus-within:border-emerald-600 transition-colors bg-accent rounded-t px-2" >
                                                <input
                                                    type="email"
                                                    id="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    required
                                                    placeholder="hello@example.com"
                                                    className="w-full bg-transparent py-3 text-sm text-foreground focus:outline-none placeholder-gray-400"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Subject */}
                                    <div className="relative group">
                                        <label htmlFor="subject" className="flex items-start gap-2 text-sm font-bold text-emerald-600 uppercase tracking-wider mb-2 transition-colors group-focus-within:text-emerald-700">
                                            {/* <FiMessageSquare className="h-4 w-4"/> */}
                                            Subject
                                        </label>
                                        <div className="flex items-center border-b-2 border-gray-100 group-focus-within:border-emerald-600 transition-colors bg-accent rounded-t px-2" >
                                            <input
                                                type="text"
                                                id="subject"
                                                name="subject"
                                                value={formData.subject}
                                                onChange={handleChange}
                                                required
                                                placeholder="How can we help you?"
                                                className="w-full bg-transparent py-3 text-sm text-foreground focus:outline-none placeholder-gray-400"
                                            />
                                        </div>
                                    </div>

                                    {/* Message */}
                                    <div className="relative group">
                                        <label htmlFor="message" className="flex items-start gap-2 text-sm font-bold text-emerald-600 uppercase tracking-wider mb-2 transition-colors group-focus-within:text-emerald-700">
                                            {/* <FiMessageSquare className="h-4 w-4"/> */}
                                            Detailed Message
                                        </label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            required
                                            rows="5"
                                            placeholder="Please describe your inquiry in detail..."
                                            className="w-full bg-accent rounded-t p-2 text-sm text-foreground border-b-2 border-gray-100 focus:border-emerald-600 transition-colors focus:outline-none focus:ring-0 placeholder-gray-400 resize-none"
                                        />
                                    </div>

                                    {/* Submit Button */}
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className={`group relative overflow-hidden py-4 px-10 font-bold text-sm tracking-widest uppercase transition-all duration-300 flex items-center justify-center space-x-3 rounded ${submitting
                                            ? 'bg-accent text-gray-400 cursor-not-allowed'
                                            : 'bg-emerald-600 text-white hover:bg-emerald-700'
                                            }`}
                                    >
                                        {submitting ? (
                                            <>
                                                <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                                                <span>Sending...</span>
                                            </>
                                        ) : (
                                            <>
                                                <FiSend className="text-lg  " />
                                                <span>Send Message</span>
                                            </>
                                        )}
                                    </button>
                                </form>
                            </div>
                        </div>
                </div>
            </main>

            <Footer navigate={navigate} navLinks={footerLinks} />
        </div>
    );
};
export default ContactPage;
